import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Edit2, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CustomAttributeDefinition, CustomAttributeFieldType } from '@/models/customAttribute';

interface DynamicInputProps {
    // Definition props
    definition?: CustomAttributeDefinition;
    fieldType?: CustomAttributeFieldType;
    label?: string;
    isRequired?: boolean;
    options?: string[];
    validationRules?: Record<string, any>;

    // Value props
    value?: any;
    onChange?: (value: any) => void;

    // Mode props
    mode?: 'form' | 'inline';
    isEditing?: boolean;
    onEdit?: () => void;
    onSave?: () => void;
    onCancel?: () => void;
    isLoading?: boolean;

    // Display props
    placeholder?: string;
    disabled?: boolean;
    className?: string;

    // Error handling
    error?: string;
    onError?: (error: string) => void;
}

export const DynamicInput: React.FC<DynamicInputProps> = ({
    definition,
    fieldType,
    label,
    isRequired = false,
    options = [],
    validationRules = {},
    value,
    onChange,
    mode = 'form',
    isEditing = false,
    onEdit,
    onSave,
    onCancel,
    isLoading = false,
    placeholder,
    disabled = false,
    className,
    error,
    onError,
}) => {
    const [internalValue, setInternalValue] = useState(value);
    const [internalError, setInternalError] = useState(error);
    const [isInternalEditing, setIsInternalEditing] = useState(isEditing);

    // Determine the actual field type and other properties
    const actualFieldType = fieldType || definition?.field_type || 'text';
    const actualLabel = label || definition?.label || 'Field';
    const actualRequired = isRequired || definition?.is_required || false;
    const actualOptions = options.length > 0 ? options : definition?.options || [];
    const actualValidationRules =
        Object.keys(validationRules).length > 0
            ? validationRules
            : definition?.validation_rules || {};

    const normalizeMultiselectValue = (rawValue: any): string[] => {
        if (Array.isArray(rawValue)) {
            return rawValue.filter(item => typeof item === 'string' && item.length > 0);
        }

        if (typeof rawValue === 'string') {
            const trimmed = rawValue.trim();
            if (!trimmed) return [];

            // Support values persisted as JSON strings in edit mode.
            if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed)) {
                        return parsed.filter(item => typeof item === 'string' && item.length > 0);
                    }
                } catch {
                    // Fallback to comma-splitting below if parsing fails.
                }
            }

            return trimmed
                .split(',')
                .map(item => item.trim())
                .filter(Boolean);
        }

        return [];
    };

    // Update internal value when prop changes
    useEffect(() => {
        if (actualFieldType === 'multiselect') {
            setInternalValue(normalizeMultiselectValue(value));
            return;
        }

        setInternalValue(value);
    }, [value, actualFieldType]);

    // Update internal error when prop changes
    useEffect(() => {
        setInternalError(error);
    }, [error]);

    // Update internal editing state when prop changes
    useEffect(() => {
        setIsInternalEditing(isEditing);
    }, [isEditing]);

    // Handle value change (only update internal state, don't call onChange)
    const handleValueChange = (newValue: any) => {
        setInternalValue(newValue);

        // Validate the new value
        const validationError = validateValue(newValue);
        if (validationError) {
            setInternalError(validationError);
            onError?.(validationError);
        } else {
            setInternalError(undefined);
            onError?.('');
        }

        // Only call onChange in form mode, not in inline mode
        if (mode === 'form') {
            onChange?.(newValue);
        }
    };

    // Validate value based on field type and rules
    const validateValue = (val: any): string | undefined => {
        if (actualRequired && (val === null || val === undefined || val === '')) {
            return `${actualLabel} is required`;
        }

        if (val === null || val === undefined || val === '') {
            return undefined; // Empty values are valid for non-required fields
        }

        switch (actualFieldType) {
            case 'text':
                if (typeof val !== 'string') {
                    return `${actualLabel} must be text`;
                }
                if (
                    actualValidationRules.min_length &&
                    val.length < actualValidationRules.min_length
                ) {
                    return `${actualLabel} must be at least ${actualValidationRules.min_length} characters`;
                }
                if (
                    actualValidationRules.max_length &&
                    val.length > actualValidationRules.max_length
                ) {
                    return `${actualLabel} must be at most ${actualValidationRules.max_length} characters`;
                }
                break;

            case 'number':
                if (isNaN(Number(val))) {
                    return `${actualLabel} must be a number`;
                }
                const numVal = Number(val);
                if (actualValidationRules.min !== undefined && numVal < actualValidationRules.min) {
                    return `${actualLabel} must be at least ${actualValidationRules.min}`;
                }
                if (actualValidationRules.max !== undefined && numVal > actualValidationRules.max) {
                    return `${actualLabel} must be at most ${actualValidationRules.max}`;
                }
                break;

            case 'date':
                if (!val || isNaN(Date.parse(val))) {
                    return `${actualLabel} must be a valid date`;
                }
                break;

            case 'boolean':
                if (typeof val !== 'boolean') {
                    return `${actualLabel} must be true or false`;
                }
                break;

            case 'select':
                if (!actualOptions.includes(val)) {
                    return `${actualLabel} must be one of the available options`;
                }
                break;

            case 'multiselect':
                if (!Array.isArray(val)) {
                    return `${actualLabel} must be an array`;
                }
                if (actualRequired && val.length === 0) {
                    return `${actualLabel} is required`;
                }
                for (const item of val) {
                    if (!actualOptions.includes(item)) {
                        return `${actualLabel} contains invalid option: ${item}`;
                    }
                }
                break;
        }

        return undefined;
    };

    // Format display value for inline mode
    const displayValue = useMemo(() => {
        if (mode === 'form') return internalValue;

        if (internalValue === null || internalValue === undefined || internalValue === '') {
            return placeholder || `Click to add ${actualLabel.toLowerCase()}`;
        }

        switch (actualFieldType) {
            case 'boolean':
                return internalValue ? 'Yes' : 'No';
            case 'date':
                return internalValue ? format(new Date(internalValue), 'MMM dd, yyyy') : '';
            case 'multiselect':
                return normalizeMultiselectValue(internalValue).join(', ');
            default:
                return String(internalValue);
        }
    }, [internalValue, actualFieldType, mode, placeholder, actualLabel]);

    // Handle edit mode toggle
    const handleEdit = () => {
        setIsInternalEditing(true);
        onEdit?.();
    };

    const handleSave = () => {
        const validationError = validateValue(internalValue);
        if (validationError) {
            setInternalError(validationError);
            onError?.(validationError);
            return;
        }

        // Call onChange with the final value when saving in inline mode
        if (mode === 'inline') {
            onChange?.(internalValue);
        }

        setIsInternalEditing(false);
        setInternalError(undefined);
        onError?.('');
        onSave?.();
    };

    const handleCancel = () => {
        setInternalValue(value); // Reset to original value
        setIsInternalEditing(false);
        setInternalError(undefined);
        onError?.('');
        onCancel?.();
    };

    // Render input based on field type
    const renderInput = () => {
        const inputProps = {
            value: internalValue || '',
            onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                handleValueChange(e.target.value);
            },
            placeholder: placeholder || `Enter ${actualLabel.toLowerCase()}`,
            disabled: disabled || isLoading,
            className: cn(
                'w-full',
                internalError && 'border-red-500 focus:border-red-500',
                mode === 'inline' && 'flex-1', // For inline mode, use flex-1
                className
            ),
        };

        switch (actualFieldType) {
            case 'text':
                return <Input {...inputProps} />;

            case 'number':
                return (
                    <Input
                        {...inputProps}
                        type="number"
                        min={actualValidationRules.min}
                        max={actualValidationRules.max}
                        step={actualValidationRules.step || '1'}
                    />
                );

            case 'date':
                return (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    'w-full justify-start text-left font-normal',
                                    !internalValue && 'text-muted-foreground',
                                    internalError && 'border-red-500 focus:border-red-500'
                                )}
                                disabled={disabled || isLoading}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {internalValue
                                    ? format(new Date(internalValue), 'PPP')
                                    : placeholder || 'Pick a date'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                            <Calendar
                                mode="single"
                                selected={internalValue ? new Date(internalValue) : undefined}
                                onSelect={date => {
                                    if (date) {
                                        handleValueChange(date.toISOString().split('T')[0]);
                                    }
                                }}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                );

            case 'boolean':
                return (
                    <div className="flex items-center space-x-2">
                        <Switch
                            checked={Boolean(internalValue)}
                            onCheckedChange={handleValueChange}
                            disabled={disabled || isLoading}
                        />
                        <Label className="text-sm font-medium">
                            {internalValue ? 'Yes' : 'No'}
                        </Label>
                    </div>
                );

            case 'select':
                return (
                    <Select
                        value={internalValue || ''}
                        onValueChange={handleValueChange}
                        disabled={disabled || isLoading}
                    >
                        <SelectTrigger
                            className={cn(
                                'w-full',
                                mode === 'inline' && 'flex-1',
                                internalError && 'border-red-500 focus:border-red-500'
                            )}
                        >
                            <SelectValue
                                placeholder={placeholder || `Select ${actualLabel.toLowerCase()}`}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {actualOptions.map(option => (
                                <SelectItem key={option} value={option}>
                                    {option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );

            case 'multiselect':
                const normalizedSelectedValues = normalizeMultiselectValue(internalValue);
                return (
                    <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                            {normalizedSelectedValues.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-md text-sm"
                                >
                                    {item}
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-auto p-0 hover:bg-primary/20"
                                        onClick={() => {
                                            const newValue = normalizedSelectedValues.filter(
                                                (_, i) => i !== index
                                            );
                                            handleValueChange(newValue);
                                        }}
                                        disabled={disabled || isLoading}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <Select
                            value=""
                            onValueChange={newValue => {
                                if (newValue && !normalizedSelectedValues.includes(newValue)) {
                                    handleValueChange([...normalizedSelectedValues, newValue]);
                                }
                            }}
                            disabled={disabled || isLoading}
                        >
                            <SelectTrigger
                                className={cn(
                                    'w-full',
                                    mode === 'inline' && 'flex-1',
                                    internalError && 'border-red-500 focus:border-red-500'
                                )}
                            >
                                <SelectValue
                                    placeholder={placeholder || `Add ${actualLabel.toLowerCase()}`}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {actualOptions
                                    .filter(option => !normalizedSelectedValues.includes(option))
                                    .map(option => (
                                        <SelectItem key={option} value={option}>
                                            {option}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                );

            default:
                return <Input {...inputProps} />;
        }
    };

    // Render form mode
    if (mode === 'form') {
        return (
            <div className="space-y-2">
                <Label className="text-sm font-medium">
                    {actualLabel}
                    {actualRequired && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {renderInput()}
                {internalError && <p className="text-sm text-red-500">{internalError}</p>}
            </div>
        );
    }

    // Render inline mode
    return (
        <div className="space-y-2">
            {!isInternalEditing ? (
                <div
                    className={cn(
                        'group cursor-pointer hover:bg-muted/50 rounded px-2 py-1 -mx-2 transition-colors',
                        !internalValue && 'text-muted-foreground',
                        className
                    )}
                    onClick={handleEdit}
                >
                    <div className="flex items-center justify-between min-w-0">
                        <span className="truncate flex-1 mr-2">{displayValue}</span>
                        <Edit2 className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <div className="flex-1">{renderInput()}</div>
                        <div className="flex gap-1">
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleSave}
                                disabled={isLoading}
                                className="h-7 w-7 p-0"
                            >
                                <Check className="h-3 w-3" />
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={handleCancel}
                                disabled={isLoading}
                                className="h-7 w-7 p-0"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                    {internalError && <p className="text-sm text-red-500">{internalError}</p>}
                </div>
            )}
        </div>
    );
};

export default DynamicInput;
